/**
 * Client-side Pokemon filter engine.
 * Builds an enriched array from pokemonData.js once, then applies
 * ParsedToken[] filters from queryParser.js to return matching Pokemon.
 */

import { POKEMON_DATA } from '../data/pokemonData';
import { LEGENDARY_IDS, MYTHICAL_IDS, BABY_IDS, STARTER_IDS, RESTRICTED_IDS, CHAMPIONS_IDS } from '../data/pokemonMeta';
import { TYPE_EFFECTIVENESS } from './pokemonTypes';
import { normaliseMoveQuery } from './queryParser';
import { LEARNSETS } from '../data/learnsets';

// ── Generation ranges ────────────────────────────────────────────────────────

const GEN_RANGES = [
  { gen: 1, min: 1,   max: 151  },
  { gen: 2, min: 152, max: 251  },
  { gen: 3, min: 252, max: 386  },
  { gen: 4, min: 387, max: 493  },
  { gen: 5, min: 494, max: 649  },
  { gen: 6, min: 650, max: 721  },
  { gen: 7, min: 722, max: 809  },
  { gen: 8, min: 810, max: 905  },
  { gen: 9, min: 906, max: 1025 },
];

function getGen(id) {
  for (const r of GEN_RANGES) {
    if (id >= r.min && id <= r.max) return r.gen;
  }
  return null; // alternate forms with IDs > 1025
}

// ── Name formatting ──────────────────────────────────────────────────────────

function formatName(key) {
  return key.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// ── Champions base-species resolution ────────────────────────────────────────
// Alternate forms and Megas have IDs >= 10000. Resolve them back to their base
// species dex number so a single CHAMPIONS_IDS set of base IDs covers all forms.
function getBaseId(key, id) {
  if (id < 10000) return id;
  const parts = key.split('-');
  for (let len = parts.length - 1; len >= 1; len--) {
    const candidate = parts.slice(0, len).join('-');
    const base = POKEMON_DATA[candidate];
    if (base && base.id < 10000) return base.id;
  }
  return id;
}

// ── Sprite ID ────────────────────────────────────────────────────────────────

// Tatsugiri megas share the same artwork — use stretchy (10324) for all
const SPRITE_REMAP = { 10322: 10324, 10323: 10324 };

function getSpriteId(key, id) {
  return SPRITE_REMAP[id] ?? id;
}

// ── Enriched array (built once and cached) ───────────────────────────────────

let _allPokemon = null;

function getAllPokemon() {
  if (_allPokemon) return _allPokemon;

  _allPokemon = Object.entries(POKEMON_DATA).map(([key, data]) => {
    const { hp, atk, def, spa, spd, spe } = data.baseStats;
    const bst = hp + atk + def + spa + spd + spe;
    const gen = getGen(data.id);

    return {
      id: data.id,
      spriteId: getSpriteId(key, data.id),
      key,
      name: formatName(key),
      types: data.types,
      baseStats: data.baseStats,
      bst,
      gen,
      abilities: data.abilities ?? [],
      weight: data.weight ?? 0,       // hectograms
      isLegendary:  LEGENDARY_IDS.has(data.id),
      isMythical:   MYTHICAL_IDS.has(data.id),
      isBaby:       BABY_IDS.has(data.id),
      isStarter:    STARTER_IDS.has(data.id),
      isRestricted: RESTRICTED_IDS.has(data.id),
      inChampions:  CHAMPIONS_IDS.has(getBaseId(key, data.id)) &&
                    !key.includes('-gmax') &&
                    !key.includes('-totem') &&
                    !key.includes('-battle-bond') &&
                    !key.includes('-ash') &&
                    !key.includes('-cap') &&
                    !key.includes('-cosplay') &&
                    !key.includes('-rock-star') &&
                    !key.includes('-belle') &&
                    !key.includes('-pop-star') &&
                    !key.includes('-phd') &&
                    !key.includes('-libre') &&
                    !key.includes('-starter'),
      isMega:       key.includes('-mega'),
      isGmax:       key.includes('-gmax'),
      // Pre-normalise move names once at build time for fast matching
      moves: (LEARNSETS[key] || []).map(m => normaliseMoveQuery(m)),
    };
  });

  return _allPokemon;
}

/** Return all unique ability names from the full Pokedex (for autocomplete). */
export function getAllAbilities() {
  const set = new Set();
  for (const p of getAllPokemon()) {
    for (const a of p.abilities) set.add(a);
  }
  return [...set].sort();
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Filter and sort the full Pokemon roster.
 * @param {{ tokens: ParsedToken[], sortBy: string, sortDir: string }} parsed
 * @returns {EnrichedPokemon[]}
 */
export function filterPokemon(parsed) {
  const { tokens, sortBy, sortDir } = parsed;
  let results = getAllPokemon();

  for (const token of tokens) {
    results = results.filter(p => matchToken(p, token));
  }

  return sortResults(results, sortBy, sortDir);
}

// ── Matching ─────────────────────────────────────────────────────────────────

function matchToken(pokemon, token) {
  const { field, op, value, negate } = token;
  let result;

  switch (field) {
    case 'name':
      result = pokemon.name.toLowerCase().includes(value) ||
               pokemon.key.includes(value);
      break;

    case 'type':
      result = pokemon.types.some(t => t.toLowerCase() === value);
      break;

    case 'id':
      result = numericCompare(op, pokemon.id, value);
      break;

    case 'gen':
      result = pokemon.gen !== null && numericCompare(op, pokemon.gen, value);
      break;

    case 'bst':
      result = numericCompare(op, pokemon.bst, value);
      break;

    case 'hp':
    case 'atk':
    case 'def':
    case 'spa':
    case 'spd':
    case 'spe':
      result = numericCompare(op, pokemon.baseStats[field], value);
      break;

    case 'ability':
      result = pokemon.abilities.some(a => a.toLowerCase().includes(value));
      break;

    case 'move': {
      // Normalise the query the same way the learnset was normalised
      const moveQuery = normaliseMoveQuery(value);
      result = pokemon.moves.some(m => m.includes(moveQuery));
      break;
    }

    case 'weight': {
      // pokemonData stores weight in hectograms; query values are in kg
      const kg = pokemon.weight / 10;
      result = numericCompare(op, kg, value);
      break;
    }

    case 'is':
      result = matchIs(pokemon, value);
      break;

    case 'in':
      result = matchIn(pokemon, value);
      break;

    case 'resists':
      result = matchResists(pokemon, value);
      break;

    case 'hits':
      result = matchHits(pokemon, value);
      break;

    case 'or-group':
      // token.tokens is an array of sub-tokens; passes if ANY matches
      result = token.tokens.some(sub => matchToken(pokemon, sub));
      break;

    default:
      result = true; // unknown field — don't filter out
  }

  return negate ? !result : result;
}

function numericCompare(op, pokemonValue, filterValue) {
  const fv = Number(filterValue);
  switch (op) {
    case ':':  return pokemonValue === fv;
    case '>':  return pokemonValue > fv;
    case '>=': return pokemonValue >= fv;
    case '<':  return pokemonValue < fv;
    case '<=': return pokemonValue <= fv;
    default:   return false;
  }
}

function matchIs(pokemon, value) {
  switch (value) {
    case 'legendary':  return pokemon.isLegendary;
    case 'mythical':   return pokemon.isMythical;
    case 'pseudo':     return pokemon.bst === 600 && !pokemon.isLegendary && !pokemon.isMythical;
    case 'baby':       return pokemon.isBaby;
    case 'starter':    return pokemon.isStarter;
    case 'mono':       return pokemon.types.length === 1;
    case 'dual':       return pokemon.types.length === 2;
    case 'mega':       return pokemon.isMega;
    case 'gmax':       return pokemon.isGmax;
    case 'restricted': return pokemon.isRestricted;
    default:           return false;
  }
}

function matchIn(pokemon, value) {
  switch (value) {
    case 'champ':
    case 'champions': return pokemon.inChampions;
    default:          return false;
  }
}

/**
 * Compute the defensive multiplier for attackType vs a Pokemon's types.
 * TYPE_EFFECTIVENESS is a sparse table; missing entries default to ×1.
 */
function defenseMultiplier(attackType, pokemonTypes) {
  const capitalType = attackType.charAt(0).toUpperCase() + attackType.slice(1);
  let mult = 1;
  for (const defType of pokemonTypes) {
    mult *= TYPE_EFFECTIVENESS[capitalType]?.[defType] ?? 1;
  }
  return mult;
}

/** resists:<type> — Pokemon takes < ×1 damage from that attack type */
function matchResists(pokemon, value) {
  return defenseMultiplier(value, pokemon.types) < 1;
}

/**
 * hits:<type> — at least one of the Pokemon's own types hits the given
 * defender type for super-effective damage (> ×1).
 */
function matchHits(pokemon, value) {
  const capitalTarget = value.charAt(0).toUpperCase() + value.slice(1);
  return pokemon.types.some(ownType => {
    const capitalOwn = ownType.charAt(0).toUpperCase() + ownType.slice(1);
    return (TYPE_EFFECTIVENESS[capitalOwn]?.[capitalTarget] ?? 1) > 1;
  });
}

// ── Sorting ──────────────────────────────────────────────────────────────────

const SORT_KEY_FN = {
  id:     p => p.id,
  name:   p => p.name,
  bst:    p => p.bst,
  hp:     p => p.baseStats.hp,
  atk:    p => p.baseStats.atk,
  def:    p => p.baseStats.def,
  spa:    p => p.baseStats.spa,
  spd:    p => p.baseStats.spd,
  spe:    p => p.baseStats.spe,
  weight: p => p.weight,
};

function sortResults(results, sortBy, sortDir) {
  const keyFn = SORT_KEY_FN[sortBy] ?? (p => p.id);
  const sign = sortDir === 'asc' ? 1 : -1;

  return [...results].sort((a, b) => {
    const av = keyFn(a);
    const bv = keyFn(b);
    if (typeof av === 'string') return sign * av.localeCompare(bv);
    return sign * (av - bv);
  });
}
